import type { DiscordActivityFloorManagerOnDuty } from '@runhuman/shared';
import { FloorManagerBadge } from './FloorManagerBadge';

interface Props {
  /**
   * Every Floor Manager on duty right now (#5169) — peers, no single winner.
   * An empty list renders the "No one on duty right now" state rather than
   * something indistinguishable from a still-loading tile. Sourced from
   * `useFloorManagerOnDuty`, which is open to every linked Activity user.
   */
  readonly floorManagersOnDuty: readonly DiscordActivityFloorManagerOnDuty[];
}

/**
 * Always-visible Floor Manager identification tile. Pinned at the top of the
 * consolidated Activity view so everyone — testers and admins alike — can see
 * who's wearing the crown without scrolling the presence roster.
 *
 * Renders EVERY on-duty FM as a list (#5169) — concurrent FMs are peers, so
 * "who do I ping?" surfaces all of them, not an arbitrary single winner.
 *
 * Independent of whether the FMs are in the Activity instance: the tile shows
 * the on-duty FMs from their schedules, so the answer is one read away even if
 * an FM happens to be elsewhere. When an FM IS in the instance, the
 * `<InstanceParticipantsHeader>` chip picks up the same badge — both surfaces
 * stay in lockstep via the shared `useFloorManagerOnDuty` React Query cache.
 */
export function FloorManagerOnDutyTile({ floorManagersOnDuty }: Props) {
  if (floorManagersOnDuty.length === 0) {
    return (
      <section className="floor-manager-tile floor-manager-tile--empty">
        <FloorManagerBadge variant="full" />
        <span className="floor-manager-tile__label">No one on duty right now</span>
      </section>
    );
  }

  return (
    <section className="floor-manager-tile">
      <FloorManagerBadge variant="full" />
      <ul className="floor-manager-tile__list">
        {floorManagersOnDuty.map((fm) => (
          <li key={fm.userId} className="floor-manager-tile__body">
            <div className="floor-manager-tile__name">{fm.displayName}</div>
            <div className="floor-manager-tile__until">until {formatTimeOfDay(fm.endAt)}</div>
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * Renders an ISO timestamp as a locale-formatted short time (e.g. "5:00 PM").
 * The block end is operationally the only meaningful detail — viewers don't
 * need the date, they need to know how long the current FM is on the hook.
 */
function formatTimeOfDay(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });
}
